import { useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '@/lib/auth-context';
import { Link } from 'react-router-dom';

interface BroadcastItem {
  id: string;
  title: string;
  body?: string;
  status: string;
  transition: string;
  duration_sec?: number;
  kind: string;
  is_urgent?: boolean;
  pin?: boolean;
  media_url?: string;
  cta_label?: string;
  cta_href?: string;
  publish_at?: string;
  expires_at?: string;
}

interface EditorProps {
  value: BroadcastItem | null;
  onChange: (updater: BroadcastItem | ((prev: BroadcastItem | null) => BroadcastItem | null)) => void;
  onSave: (row?: BroadcastItem) => Promise<void>;
  onPublishNow: () => Promise<void>;
  onUrgent: (on: boolean) => Promise<void>;
  onDuplicate: () => Promise<void>;
  onDelete: () => Promise<void>;
  saving: boolean;
}

const TRANSITIONS = ['fade', 'slide', 'zoom', 'marquee'];
const STATUSES = ['draft', 'scheduled', 'published'];

export default function Broadcasts() {
  const { profile, session } = useAuthContext();
  const bearer = session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : null;

  const [items, setItems] = useState<BroadcastItem[]>([]);
  const [q, setQ] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [draft, setDraft] = useState<BroadcastItem | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [dragIds, setDragIds] = useState<string[]>([]);

  const isAdmin = ['admin', 'superadmin'].includes(profile?.role || '');
  if (!isAdmin) return <div className="p-6">You do not have permission to view this page.</div>;

  async function load() {
    if (!bearer) return;
    setLoading(true);
    try {
      const u = new URL('/api/v1/admin/broadcasts', window.location.origin);
      const res = await fetch(u, { headers: { ...bearer } });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error?.message || json?.error || 'Failed');
      const raw = Array.isArray(json?.data) ? json.data : Array.isArray(json?.items) ? json.items : [];
      // Client-side filter since Render endpoint doesn't support q/status params
      const list = raw.filter((x: BroadcastItem) => {
        if (status && x.status !== status) return false;
        if (q && !x.title?.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
      });
      setItems(list);
      setDragIds(list.map((x: BroadcastItem) => x.id));
      setDraft((prev) => {
        if (!prev?.id) return prev;
        const next = list.find((item: BroadcastItem) => item.id === prev.id);
        return next || prev;
      });
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, session?.access_token]);

  async function save(row: BroadcastItem | null = draft) {
    if (!row) return;
    setSaving(true);
    try {
      const res = await fetch('/api/v1/admin/broadcasts', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...bearer },
        body: JSON.stringify(row),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error?.message || json?.error || 'Save failed');
      setDraft(json?.data || json?.item || row);
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function publishNow() {
    if (!draft) return;
    const next = { ...draft, status: 'published', publish_at: new Date().toISOString() };
    setDraft(next);
    await save(next);
  }

  async function markUrgent(on: boolean = true) {
    if (!draft) return;
    const res = await fetch(`/api/v1/admin/broadcasts/${draft.id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', ...bearer },
      body: JSON.stringify({ is_urgent: !!on }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return alert(json?.error || 'Failed to toggle urgent');
    setDraft((prev) => {
      if (!prev || prev.id !== draft.id) return prev;
      return { ...prev, is_urgent: !!on };
    });
    await load();
  }

  async function duplicateCurrent() {
    if (!draft?.id) return;
    const res = await fetch(`/api/v1/admin/broadcasts/${draft.id}/duplicate`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...bearer },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return alert(json?.error?.message || json?.error || 'Duplicate failed');
    setDraft(json.item || json.data);
    await load();
  }

  async function deleteCurrent() {
    if (!draft?.id) return;
    if (!confirm('Delete this broadcast? This cannot be undone.')) return;
    const res = await fetch(`/api/v1/admin/broadcasts/${draft.id}`, {
      method: 'DELETE',
      headers: { ...bearer },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return alert(json?.error?.message || json?.error || 'Delete failed');
    setDraft(null);
    await load();
  }

  function onDragStart(e: React.DragEvent<HTMLDivElement>, id: string) {
    e.dataTransfer.setData('text/plain', id);
  }
  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
  }
  function onDrop(e: React.DragEvent<HTMLDivElement>, overId: string) {
    const id = e.dataTransfer.getData('text/plain');
    if (!id || id === overId) return;
    const next = [...dragIds];
    const from = next.indexOf(id);
    const to = next.indexOf(overId);
    next.splice(from, 1);
    next.splice(to, 0, id);
    setDragIds(next);
  }
  async function applyReorder() {
    if (!dragIds.length) return;
    const res = await fetch('/api/v1/admin/broadcasts/reorder', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...bearer },
      body: JSON.stringify({ ids: dragIds }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return alert(json?.error || 'Failed to reorder');
    await load();
  }

  const queue = useMemo((): BroadcastItem[] => {
    if (!dragIds.length) return items;
    const map = new Map(items.map((item: BroadcastItem) => [item.id, item]));
    return dragIds.map((id: string) => map.get(id)).filter((item): item is BroadcastItem => Boolean(item));
  }, [items, dragIds]);

  return (
    <div className="p-4 space-y-4">
      <Link to="/portal/admin" className="text-sm underline text-cocoa">
        ← Admin
      </Link>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="md:w-1/2 border rounded-lg bg-white">
          <div className="p-3 flex items-center gap-2 border-b">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search title…"
              className="border rounded px-2 py-1 w-full"
            />
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded px-2 py-1">
              <option value="">All</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button
              onClick={() =>
                setDraft({
                  id: '',
                  title: 'New slide',
                  status: 'draft',
                  transition: 'fade',
                  duration_sec: 10,
                  kind: 'content',
                })
              }
              className="px-3 py-1 border rounded"
            >
              + New
            </button>
          </div>

          <div className="max-h-[70vh] overflow-auto">
            {loading && <div className="p-3 text-sm">Loading…</div>}
            {!loading &&
              queue.map((x) => {
                const urgent = x.is_urgent ? 'bg-red-50' : '';
                return (
                  <div
                    key={x.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, x.id)}
                    onDragOver={onDragOver}
                    onDrop={(e) => onDrop(e, x.id)}
                    className={`p-3 border-b flex items-center justify-between hover:bg-gray-50 ${urgent}`}
                  >
                    <div onClick={() => setDraft(x)} className="cursor-pointer">
                      <div className="font-medium">{x.title}</div>
                      <div className="text-xs text-cocoa/70">
                        {x.status} • {x.transition} • {x.duration_sec || 10}s{' '}
                        {x.is_urgent && (
                          <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px]">URGENT</span>
                        )}
                        {x.pin && (
                          <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px]">PINNED</span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-cocoa/60">#{queue.findIndex((i: BroadcastItem) => i.id === x.id) + 1}</div>
                  </div>
                );
              })}
          </div>

          <div className="p-3 border-t flex items-center justify-end">
            <button onClick={applyReorder} className="px-3 py-1 rounded bg-terracotta text-white">
              Apply order
            </button>
          </div>
        </div>

        <div className="md:w-1/2 border rounded-lg bg-white p-4">
          {!draft && <div className="text-sm text-cocoa/60">Select or create a slide.</div>}
          {draft && (
            <Editor
              value={draft}
              onChange={setDraft}
              onSave={save}
              onPublishNow={publishNow}
              onUrgent={markUrgent}
              onDuplicate={duplicateCurrent}
              onDelete={deleteCurrent}
              saving={saving}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Editor({ value, onChange, onSave, onPublishNow, onUrgent, onDuplicate, onDelete, saving }: EditorProps) {
  const v: BroadcastItem = value || { id: '', title: '', status: 'draft', transition: 'fade', kind: 'content' };
  const [publishAtLocal, setPublishAtLocal] = useState<string>(
    v.publish_at ? new Date(v.publish_at).toISOString().slice(0, 16) : ''
  );

  useEffect(() => {
    setPublishAtLocal(v.publish_at ? new Date(v.publish_at).toISOString().slice(0, 16) : '');
  }, [v.publish_at]);

  const apply = (updater: BroadcastItem | ((prev: BroadcastItem) => BroadcastItem)) => {
    onChange((prev) => {
      const base: BroadcastItem = prev ?? v;
      return typeof updater === 'function' ? updater(base) : updater;
    });
  };

  const setField = (k: keyof BroadcastItem, val: unknown) => apply((curr) => ({ ...curr, [k]: val }));

  function seedRandomizer() {
    const sample = ['Alex', 'Jordan', 'Taylor', 'Casey', 'Morgan', 'Riley', 'Parker', 'Drew', 'Quinn', 'Sky'];
    const names = Array.from({ length: 5 }, () => ({
      n: sample[(Math.random() * sample.length) | 0],
      w: (1 + (Math.random() * 3) | 0),
    }));
    apply((curr) => ({
      ...curr,
      kind: 'randomizer',
      title: 'Tonight’s Classroom Draw',
      body: JSON.stringify({ names, seed: Math.floor(Math.random() * 1e6) }, null, 2),
      transition: 'zoom',
      duration_sec: 12,
    }));
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs mb-1">Title</label>
          <input
            className="w-full border rounded px-2 py-1"
            value={v.title || ''}
            onChange={(e) => setField('title', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs mb-1">Kind</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={v.kind || 'content'}
            onChange={(e) => setField('kind', e.target.value)}
          >
            {['content', 'class_fill', 'randomizer', 'donations', 'users'].map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs mb-1">Status</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={v.status || 'draft'}
            onChange={(e) => setField('status', e.target.value)}
          >
            {['draft', 'scheduled', 'published'].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs mb-1">Transition</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={v.transition || 'fade'}
            onChange={(e) => setField('transition', e.target.value)}
          >
            {TRANSITIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs mb-1">Body (text or JSON for randomizer)</label>
        <textarea
          className="w-full border rounded px-2 py-1 min-h-[120px]"
          value={v.body || ''}
          onChange={(e) => setField('body', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs mb-1">Media URL (optional)</label>
          <input
            className="w-full border rounded px-2 py-1"
            value={v.media_url || ''}
            onChange={(e) => setField('media_url', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs mb-1">CTA Label</label>
            <input
              className="w-full border rounded px-2 py-1"
              value={v.cta_label || ''}
              onChange={(e) => setField('cta_label', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs mb-1">CTA Href</label>
            <input
              className="w-full border rounded px-2 py-1"
              value={v.cta_href || ''}
              onChange={(e) => setField('cta_href', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-xs mb-1">Duration (sec)</label>
          <input
            type="number"
            min={3}
            max={120}
            className="w-full border rounded px-2 py-1"
            value={v.duration_sec || 10}
            onChange={(e) =>
              setField('duration_sec', Number.parseInt(e.target.value || '10', 10) || 10)
            }
          />
        </div>
        <div className="flex items-end gap-2">
          <label className="text-xs mr-2">Pin</label>
          <input type="checkbox" checked={!!v.pin} onChange={(e) => setField('pin', !!e.target.checked)} />
          <label className="text-xs ml-4 mr-2">Urgent</label>
          <input
            type="checkbox"
            checked={!!v.is_urgent}
            onChange={(e) => setField('is_urgent', !!e.target.checked)}
          />
        </div>
        <div className="flex items-end justify-end gap-2">
          <button onClick={seedRandomizer} className="px-2 py-1 border rounded">
            Seed Randomizer
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className="block text-xs">Publish At</label>
          <div className="flex gap-2">
            <input
              type="datetime-local"
              className="w-full border rounded px-2 py-1"
              value={publishAtLocal}
              onChange={(e) => setPublishAtLocal(e.target.value)}
            />
            <button
              type="button"
              className="px-2 py-1 border rounded"
              onClick={() => {
                const iso = publishAtLocal ? new Date(publishAtLocal).toISOString() : undefined;
                apply((curr) => ({ ...curr, publish_at: iso }));
              }}
            >
              Apply
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs mb-1">Expires At</label>
          <input
            type="datetime-local"
            className="w-full border rounded px-2 py-1"
            value={v.expires_at ? new Date(v.expires_at).toISOString().slice(0, 16) : ''}
            onChange={(e) =>
              setField('expires_at', e.target.value ? new Date(e.target.value).toISOString() : null)
            }
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-2">
        <button
          disabled={saving}
          onClick={() => onSave()}
          className="px-3 py-2 rounded bg-terracotta text-white disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onPublishNow} className="px-3 py-2 rounded border">Publish Now</button>
        <button onClick={() => onUrgent(true)} className="px-3 py-2 rounded border">Mark Urgent</button>
        <button onClick={() => onUrgent(false)} className="px-3 py-2 rounded border">Clear Urgent</button>
        <span className="mx-2 opacity-40">|</span>
        <button onClick={onDuplicate} className="px-3 py-2 rounded border">Duplicate</button>
        <button onClick={onDelete} className="px-3 py-2 rounded border text-red-600">Delete</button>
      </div>
    </div>
  );
}
